import { Component, inject, model, output, input, viewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SideDialogComponent } from "@kernel/popups/popups.config";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { CheckboxListComponent } from "@kernel/form/modules/inputs/simple-inputs/components/checkbox-list/checkbox-list.component";
import { ButtonComponent } from "@kernel/shared/modules/button/components/button/button.component";
import { NumberInputComponent } from "@kernel/form/modules/inputs/simple-inputs/components/number-input/number-input.component";
import { homeTypes } from "@app/shared/swap-request/static-data/home-types.constant";
import { homeFeatures } from "@app/shared/swap-request/static-data/home-features.constant";
import { TranslateModule, TranslatePipe } from "@ngx-translate/core";
import { SxwFormComponent } from "@kernel/form/components/sxw-form/sxw-form.component";
import { PropertyFiltersFormModel } from "@app/core/filtering/models/property-filters-form.model";
import { FilterChangesInterface } from "@kernel/listing/interfaces/filters.interface";
import { AddressesAutocompleteComponent } from "@kernel/form/modules/inputs/addresses-autocomplete/components/addresses-autocomplete/addresses-autocomplete.component";
import { RangeInputComponent } from "@app/shared/general/components/range-input/range-input.component";
import { PropertyMatchOptionsInputComponent } from "@app/core/filtering/components/property-match-options-input/property-match-options-input.component";
import { PropertyFiltersService } from "@app/core/filtering/services/property-filters.service";

@Component({
  selector: "app-property-filters",
  templateUrl: "./property-filters.component.html",
  styleUrls: ["./property-filters.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SideDialogComponent,
    SvgIconComponent,
    CheckboxListComponent,
    ButtonComponent,
    NumberInputComponent,
    TranslateModule,
    SxwFormComponent,
    ReactiveFormsModule,
    TranslatePipe,
    AddressesAutocompleteComponent,
    RangeInputComponent,
    PropertyMatchOptionsInputComponent,
  ],
})
export class PropertyFiltersComponent {
  /** Controls the visibility state of the filters dialog */
  visible = model<boolean>(false);

  /** Determines how multiple filters are combined: logical AND or OR */
  condition = input<"AND" | "OR">("AND");

  /** Emits whenever filters are applied or updated */
  filtersApplied = output<FilterChangesInterface | undefined>();

  /** Reference to the address autocomplete component in the template */
  addressElement = viewChild<AddressesAutocompleteComponent>("addressElement");

  /** Injected form model responsible for managing filter form state */
  formModel = inject(PropertyFiltersFormModel);

  /** Service responsible for managing property filters */
  filtersService = inject(PropertyFiltersService);

  /** Signal holding the currently applied property filters. Sourced from `PropertyFiltersService`. */
  appliedFilters = this.filtersService.appliedFilters;

  /** List of available home types (static reference) */
  readonly homeTypes = homeTypes;

  /** List of available home features (static reference) */
  readonly homeFeatures = homeFeatures;

  /**
   * Applies the current form values as active filters.
   *
   * - Updates the internal filter state
   * - Generates preview labels
   * - Emits the updated filters to the parent component
   */
  applyFilters(): void {
    this.filtersService.applyFilters(
      { condition: this.condition(), filters: this.formModel.getCurrentDtoInput() },
      this.formModel.getCurrentFiltersPreview(),
      this.formModel.getCurrentFormValue()
    );
    this.filtersApplied.emit(this.appliedFilters());
  }

  /**
   * Clears all filter inputs and reapplies the empty filter state.
   * Also closes the filters dialog.
   */
  clearFilters(): void {
    this.formModel.formGroup().reset();
    this.applyFilters();
    this.hideDialog();
  }

  /**
   * Adds a city to the filters using a Google Places result.
   *
   * Extracts the locality name and ensures valid coordinates exist
   * before updating the form model.
   *
   * @param location - Optional Google Maps place result.
   */
  addCity(location?: google.maps.places.PlaceResult): void {
    const city = location?.address_components?.find(c => c.types.includes("locality"))?.long_name ?? location?.name;

    if (!city || !location?.geometry?.location?.lat() || !location?.geometry?.location?.lng()) {
      return;
    }

    // Clear the autocomplete input after selection
    this.addressElement()?.clearInput();
    this.formModel.addCity(city);
  }

  /**
   * Removes a city from the selected cities list.
   *
   * @param index - Index of the city to remove.
   */
  removeCity(index: number): void {
    this.formModel.removeCity(index);
  }

  /**
   * Submits the current filters and closes the dialog.
   */
  submitFilters(): void {
    this.applyFilters();
    this.hideDialog();
  }

  /**
   * Hides the filters dialog.
   */
  hideDialog(): void {
    this.visible.set(false);
  }
}
